import {
  ColumnFactory,
  createIconCellFormatter,
  createSelectionColumn,
  createSortableHeaderFormatter,
  createTextCellFormatter,
  ExplorerStyledDisplayGrid,
  GraphQLError,
  GridSortProps,
  RemoteTypeMappings,
  reportGraphQLErrors,
  SelectionProviderChildProps,
  UnsortableColumnKey,
  UsualDisplayGridProps,
} from '@tableau/data-catalog-shared';
import * as React from 'react';

import { Messages } from '../../../gen-src/Localize';
import { ColumnOrderField, RemoteType } from '../../../gen-src/queries/globalTypes';
import { createNumberCellFormatter } from '../datagrid/cellFormatters/NumberCellFormatter';
import {
  createQueryPreservingReplacingLinkCellFormatter,
} from '../datagrid/cellFormatters/QueryPreservingLinkCellFormatter';
import { RemoteTypeIcon } from '../datagrid/cellFormatters/RemoteTypeIcon';
import { createTagCellFormatter } from '../datagrid/cellFormatters/TagCellFormatter';
import { ColumnsValidSortFields } from '../sorting/sortProviders/ColumnsGridSortProvider';

export interface TableColumnsGridRowData {
  readonly tableId: string;
  readonly id: string;
  readonly tags: ReadonlyArray<string> | null;
  readonly name: string | null;
  readonly remoteType: RemoteType;
  readonly description: string | null;
  readonly datasourceCount: number | null;
  readonly sheetCount: number | null;
}


type TableColumnsColumnFactory = ColumnFactory<
  GridSortProps<ColumnsValidSortFields>,
  TableColumnsGridRowData,
  ColumnsValidSortFields
>;

function columns(): ReadonlyArray<TableColumnsColumnFactory> {
  const cols: ReadonlyArray<TableColumnsColumnFactory | false> = [
    () => createSelectionColumn(),
    (sortParams) => ({
      name: 'type',
      dataKey: UnsortableColumnKey,
      headerText: Messages.GridHeaderType(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createIconCellFormatter(
        ({ remoteType }) => <RemoteTypeIcon remoteType={remoteType} />,
        ({ remoteType }) => RemoteTypeMappings[remoteType](),
      ),
      resizable: false,
      sortable: false,
    }),
    (sortParams) => ({
      name: 'name',
      dataKey: ColumnOrderField.NAME,
      headerText: Messages.GridHeaderName(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createQueryPreservingReplacingLinkCellFormatter(
        ({ name }) => name || '',
        (urls, { id, tableId }) => urls.catalogTableColumnPath(tableId, id),
      ),
      resizable: true,
      sortable: true,
    }),
    (sortParams) => ({
      name: 'connectedSheets',
      dataKey: UnsortableColumnKey,
      headerText: Messages.GridHeaderSheets(),
      headerFormatter: createSortableHeaderFormatter(sortParams, 'right'),
      cellFormatter: createNumberCellFormatter(({ sheetCount }) => sheetCount),
      sortable: false,
    }),
    (sortParams) => ({
      name: 'connectedDatasources',
      dataKey: UnsortableColumnKey,
      headerText: Messages.GridHeaderDatasources(),
      headerFormatter: createSortableHeaderFormatter(sortParams, 'right'),
      cellFormatter: createNumberCellFormatter(({ datasourceCount }) => datasourceCount),
      sortable: false,
    }),
    (sortParams) => ({
      name: 'description',
      dataKey: UnsortableColumnKey,
      headerText: Messages.GridHeaderDescription(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createTextCellFormatter(({ description }) => description, Messages.NoDescriptionGridPlaceholder()),
      sortable: false,
      growToFit: true,
    }),
    (sortParams) => ({
      name: 'tags',
      dataKey: UnsortableColumnKey,
      headerText: Messages.GridHeaderTags(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createTagCellFormatter(({ tags }) => tags ?? []),
      sortable: false,
      growToFit: true
    }),
  ];

  return cols.filter(Boolean) as ReadonlyArray<TableColumnsColumnFactory>;
}

export type TableColumnsDisplayGridProps =
  UsualDisplayGridProps<TableColumnsGridRowData, GraphQLError> &
  SelectionProviderChildProps &
  GridSortProps<ColumnsValidSortFields>;

export function TableColumnsDisplayGrid(props: TableColumnsDisplayGridProps): JSX.Element {
  const cols = columns().map(columnFactory => columnFactory(props));
  return (
    <ExplorerStyledDisplayGrid
      columns={cols}
      rowStore={props.rowStore}
      selectedKeys={props.selectedKeys}
      updateSelectionForKey={props.updateSelectionForKey}
      rowCount={props.rowCount}
      renderErrors={reportGraphQLErrors}
      orderBy={props.orderBy}
      updateSortOrder={props.updateSortOrder}
    />
  );
}
